/** @jsxImportSource react */
import { useState } from 'react'
import { Box, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { recordsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { useEntityAccess } from '../../hooks/usePermissions'
import { formatValue } from './fieldValues'
import { LookupLink } from './LookupInput'
import Loading from '../common/Loading'
import { entityLabel } from '../../utils/helpers'

const LEVEL_SIZE = 100
const INDENT = 3 // theme spacing units per level
const NUMERIC = ['NUMBER', 'DECIMAL', 'CURRENCY', 'ROLLUP']

/** One level of the tree: the records whose parent link is `parentId` (null = top level). */
const Level = ({ entityType, treeField, parentId, depth, columns, sort, formCode }) => {
  const tenantId = useTenantKey()
  const filters = { [treeField.fieldKey]: parentId ? { eq: parentId } : { empty: 'true' } }
  const { data, isLoading } = useQuery({
    queryKey: [tenantId, 'records', entityType, 'tree', parentId, sort],
    queryFn: () => recordsAPI.list({ entityType, filters, sort, size: LEVEL_SIZE }),
  })
  const ids = data?.items.map((r) => r.id) || []
  const { data: counts = {} } = useQuery({
    queryKey: [tenantId, 'records', entityType, 'tree-counts', parentId, ids.join(',')],
    queryFn: () => recordsAPI.childCounts(entityType, treeField.fieldKey, ids),
    enabled: ids.length > 0,
  })

  if (isLoading) return <Box sx={{ pl: depth * INDENT }}><Loading /></Box>
  if (!data) return null
  return (
    <>
      {data.items.map((r) => (
        <Node key={r.id} record={r} entityType={entityType} treeField={treeField} depth={depth} columns={columns}
          sort={sort} childCount={counts[r.id] || 0} formCode={formCode} />
      ))}
      {data.totalItems > data.items.length && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: depth * INDENT + 5, py: 0.5 }}>
          Showing {data.items.length} of {data.totalItems}; use the table view and filters to find the rest.
        </Typography>
      )}
    </>
  )
}

const Node = ({ record, entityType, treeField, depth, columns, sort, childCount, formCode }) => {
  const navigate = useNavigate()
  const access = useEntityAccess(entityType)
  const [open, setOpen] = useState(false)
  const newChild = `/records/${entityType}/new?form=${encodeURIComponent(formCode)}&prefill.${treeField.fieldKey}=${record.id}`
  return (
    <>
      <Box role="treeitem" aria-expanded={childCount ? open : undefined} aria-level={depth + 1}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, pl: depth * INDENT, pr: 1, py: 0.5, borderBottom: 1, borderColor: 'divider',
          '&:hover': { bgcolor: 'action.hover' }, '&:hover .tree-add': { opacity: 1 },
        }}>
        <Box sx={{ width: 32, flexShrink: 0 }}>
          {childCount > 0 && (
            <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? 'Collapse' : 'Expand'}>
              {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => navigate(`/records/${entityType}/${record.id}`)}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
            {record.title}
            {childCount > 0 && <Typography component="span" variant="caption" color="text.secondary"> · {childCount}</Typography>}
          </Typography>
        </Box>
        {columns.map((f) => (
          <Typography key={f.fieldKey} variant="body2" color="text.secondary" noWrap
            sx={{ width: 160, flexShrink: 0, display: { xs: 'none', md: 'block' }, textAlign: NUMERIC.includes(f.fieldType) ? 'right' : 'left' }}>
            {f.fieldType === 'LOOKUP' || f.fieldType === 'USER' ? <LookupLink refValue={record.lookups?.[f.fieldKey]} /> : formatValue(f, record.data[f.fieldKey])}
          </Typography>
        ))}
        {access.canCreate && (
          <Tooltip title={`Add a sub-${entityLabel(entityType).toLowerCase()}`}>
            <IconButton className="tree-add" size="small" sx={{ opacity: { xs: 1, md: 0 } }} onClick={() => navigate(newChild)} aria-label="Add sub-record">
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {open && <Level entityType={entityType} treeField={treeField} parentId={record.id} depth={depth + 1} columns={columns} sort={sort} formCode={formCode} />}
    </>
  )
}

/**
 * Tree layout for a record type whose parent link points at itself (accounts → sub-accounts).
 * Each level loads when it is expanded.
 */
const RecordTree = ({ entityType, treeField, columns, sort, formCode }) => (
  <Paper variant="outlined" role="tree" aria-label={`${entityLabel(entityType)} tree`}>
    <Box sx={{ display: 'flex', gap: 1, px: 1, py: 1, borderBottom: 1, borderColor: 'divider', pl: 5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>Name</Typography>
      {columns.map((f) => (
        <Typography key={f.fieldKey} variant="caption" color="text.secondary" noWrap
          sx={{ width: 160, display: { xs: 'none', md: 'block' }, textAlign: NUMERIC.includes(f.fieldType) ? 'right' : 'left' }}>{f.label}</Typography>
      ))}
      <Box sx={{ width: 34 }} />
    </Box>
    <Level entityType={entityType} treeField={treeField} parentId={null} depth={0} columns={columns} sort={sort} formCode={formCode} />
  </Paper>
)

export default RecordTree
